import {ListService} from '../Types/service';

export const getBuColLabel = (info: any) => {
  const row = info.row.original;

  if (!row.buId && !row.codeX3 && !row.buName) return '-';

  const name = row.buName || row.codeX3 || '';

  return (
    <span className="d-flex align-items-start" style={{minWidth: 0}}>
      <i className="bi bi-building me-2 mt-1 text-gray-600 flex-shrink-0" />
      <div className="d-flex flex-column" style={{minWidth: 0}}>
        <span className="text-truncate d-block" title={name}>
          {name}
        </span>
        {row.buId && (
          <span className="text-muted fs-7 text-truncate d-block" title={`ID: ${row.buId}`}>
            ID: {row.buId}
          </span>
        )}
      </div>
    </span>
  );
};

export const getSiteColLabel = (info: any) => {
  const site = info.row.original.site;

  if (!site) return '-';

  const address = [site.address, site.zipCode, site.city].filter(Boolean).join(' ');
  const subtitle = [site.id ? `ID: ${site.id}` : null, site.company].filter(Boolean).join(' - ');

  return (
    <span className="d-flex align-items-start" style={{minWidth: 0}}>
      <i className="bi bi-geo-alt me-2 mt-1 text-gray-600 flex-shrink-0" />
      <div className="d-flex flex-column" style={{minWidth: 0}}>
        <span className="text-truncate d-block" title={address}>
          {address}
        </span>
        <span className="text-muted fs-7 text-truncate d-block" title={subtitle}>
          {subtitle}
        </span>
      </div>
    </span>
  );
};

export const getServiceColLabel = (info: any) => {
  const row = info.row.original;
  const isDomainOrBay =
    row.is_domain || row.type?.some((t: string) => t.toLowerCase().includes('bay'));
  const isVpnOrLien = row.type?.some((t: string) => t === 'VPN' || t === 'Connexion');
  const name = isDomainOrBay ? row.link_name : row.serviceName;
  const serviceId = row.serviceId;
  const subtitle = [`ID: ${serviceId}`, isVpnOrLien ? row.link_name : null]
    .filter(Boolean)
    .join(' - ');
  return (
    <span className="d-flex align-items-start" style={{minWidth: 0}}>
      <i className="bi bi-hdd-network me-2 mt-1 text-gray-600 flex-shrink-0" />
      <div className="d-flex flex-column" style={{minWidth: 0}}>
        <span className="text-truncate d-block" title={name}>
          {name}
        </span>
        <span className="text-muted fs-7 text-truncate d-block" title={subtitle}>
          {subtitle}
        </span>
      </div>
    </span>
  );
};

export const getServiceColLabelOnly = (info: any) => {
  if (
    info.row.original.is_domain ||
    info.row.original.type?.some((t: string) => t.toLowerCase().includes('bay'))
  )
    return `${info.row.original.link_name}`;
  return `${info.row.original.serviceName}`;
};

export const accessorSite = (row: any) => {
  return `${row.site?.id ?? ''} ${row.site?.address ?? ''} ${row.site?.zipCode ?? ''} ${
    row.site?.city ?? ''
  } ${row.site?.codeX3 ?? ''}`;
};

export const accessorService = (row: any) => {
  const isDomainOrBay =
    row.is_domain || row.type?.some((t: string) => t.toLowerCase().includes('bay'));
  const isVpnOrLien = row.type?.some((t: string) => t === 'VPN' || t === 'Connexion');
  const name = isDomainOrBay ? row.link_name : row.serviceName;
  return `${row.serviceId ?? ''} ${name ?? ''} ${isVpnOrLien ? row.link_name ?? '' : ''}`;
};

const TELEPHONY_PILOT_BU_IDS_STORAGE_KEY = 'telephonyPilotBuIds';

/**
 * Surcharge locale (navigateur) de la liste des BU pilotes téléphonie, en
 * complément de `row.isTelephonyMigrated` (CIS) — utile pour tester l'IHM
 * interne sur un service qui n'est pas encore marqué migré côté CIS.
 */
const getLocalTelephonyPilotBuIds = (): number[] => {
  try {
    const raw = localStorage.getItem(TELEPHONY_PILOT_BU_IDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
};

/**
 * Ajoute un BU à la liste locale de pilotes sans écraser ceux déjà ajoutés
 * (contrairement à un `localStorage.setItem` direct, qui remplacerait toute
 * la liste). Exposée sur `window` pour un usage depuis la console DevTools :
 * `addTelephonyPilotBu(12345)`.
 */
const addLocalTelephonyPilotBuId = (buId: number): void => {
  const current = getLocalTelephonyPilotBuIds();
  if (!current.includes(buId)) {
    localStorage.setItem(TELEPHONY_PILOT_BU_IDS_STORAGE_KEY, JSON.stringify([...current, buId]));
  }
};

/**
 * Retire un BU de la liste locale de pilotes sans toucher aux autres.
 * Exposée sur `window` : `removeTelephonyPilotBu(12345)`.
 */
const removeLocalTelephonyPilotBuId = (buId: number): void => {
  const remaining = getLocalTelephonyPilotBuIds().filter((id) => id !== buId);
  if (remaining.length > 0) {
    localStorage.setItem(TELEPHONY_PILOT_BU_IDS_STORAGE_KEY, JSON.stringify(remaining));
  } else {
    localStorage.removeItem(TELEPHONY_PILOT_BU_IDS_STORAGE_KEY);
  }
};

declare global {
  interface Window {
    addTelephonyPilotBu: (buId: number) => void;
    removeTelephonyPilotBu: (buId: number) => void;
  }
}

window.addTelephonyPilotBu = addLocalTelephonyPilotBuId;
window.removeTelephonyPilotBu = removeLocalTelephonyPilotBuId;

export const isTrunkSipServiceName = (serviceName?: string | null): boolean =>
  (serviceName ?? '').toLowerCase().includes('trunk sip');

export const isCentrexServiceName = (serviceName?: string | null): boolean =>
  (serviceName ?? '').toLowerCase().includes('centrex');

export const isPabxServiceName = (serviceName?: string | null): boolean =>
  (serviceName ?? '').toLowerCase().includes('pabx');

/**
 * CIS publie ce type de prestation sous le nom « Téléphonie IP » — « Téléphonie
 * Essentielle » n'était qu'un nom de maquette/dev, jamais le nom réel côté CIS.
 */
export const isEssentielleServiceName = (serviceName?: string | null): boolean => {
  const lower = (serviceName ?? '').toLowerCase();
  return lower.includes('essentielle') || lower.includes('téléphonie ip');
};

export const shouldOpenInternalTelephonyDetails = (row: ListService): boolean => {
  const isMaquetteDetail =
    isTrunkSipServiceName(row.serviceName) ||
    isCentrexServiceName(row.serviceName) ||
    isEssentielleServiceName(row.serviceName);
  const isPilotBu =
    row.isTelephonyMigrated === true || getLocalTelephonyPilotBuIds().includes(row.buId);

  return isMaquetteDetail && isPilotBu;
};

/** Chemin de la page détail d'un service téléphonie (`/services/telephonie/{serviceId}`). */
export const getTelephonyDetailsPathname = (serviceId: number | string): string =>
  `/services/telephonie/${serviceId}`;

/** URL de la page détail téléphonie ; `trunkSip` évite d'attendre les infos du service pour afficher les onglets Trunk SIP / Centrex. */
export const getTelephonyDetailsPath = (row: ListService): string => {
  const pathname = getTelephonyDetailsPathname(row.serviceId);
  return shouldOpenInternalTelephonyDetails(row) ? `${pathname}?trunkSip=1` : pathname;
};

export const DisplaySite = (service: any) => {
  if (service?.site?.id == null) return 'N/A';
  let title = `${service?.site?.id} - `;
  if (service.codeX3) title += `${service.codeX3} - `;
  title += `${service?.site?.address} ${service?.site?.zipCode} ${service?.site?.city}`;
  return title;
};
